import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { DataTable } from '../../../../shared/components/index.js';

interface ChannelStat { readonly channel: string; readonly sent: number; readonly delivered: number; readonly failed: number; }
interface DashboardData {
  readonly totalSent: number;
  readonly delivered: number;
  readonly failed: number;
  readonly pending: number;
  readonly byChannel: readonly ChannelStat[];
}

export function NotificationsDashboardPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<DashboardData>('/notifications/dashboard', { token })
      .then((r) => { if (alive) setData(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('notif.common.loading')}</p>;
  return (
    <main aria-label="notification-dashboard">
      <h1>{t('notif.dash.title')}</h1>
      <section aria-label="summary-cards" className="card-grid">
        <article className="card"><h2>{t('notif.common.sent')}</h2><p data-testid="kpi-sent">{data.totalSent}</p></article>
        <article className="card"><h2>{t('notif.common.delivered')}</h2><p data-testid="kpi-delivered">{data.delivered}</p></article>
        <article className="card"><h2>{t('notif.common.failed')}</h2><p data-testid="kpi-failed">{data.failed}</p></article>
        <article className="card"><h2>{t('notif.dash.pending')}</h2><p data-testid="kpi-pending">{data.pending}</p></article>
      </section>
      <h2>{t('notif.dash.byChannel')}</h2>
      <DataTable<ChannelStat>
        ariaLabel="channel-breakdown"
        columns={[
          { key: 'channel', header: t('notif.common.channel'), render: (c) => c.channel },
          { key: 'sent', header: t('notif.common.sent'), render: (c) => c.sent },
          { key: 'delivered', header: t('notif.common.delivered'), render: (c) => c.delivered },
          { key: 'failed', header: t('notif.common.failed'), render: (c) => c.failed },
        ]}
        rows={data.byChannel}
        rowKey={(c) => c.channel}
      />
    </main>
  );
}
